import { useEffect, useRef, useState, useCallback } from "react";
import { BACKEND_URL } from "../config/api";

/**
 * useWebSocket — connects to the CRM WebSocket and returns live events.
 * Auto-reconnects with exponential backoff.
 */
export default function useWebSocket(userId) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const reconnectTimer = useRef(null);
  const retryCount = useRef(0);
  const listeners = useRef(new Map());

  const getWsUrl = useCallback(() => {
    const base = BACKEND_URL.replace(/^http/, "ws");
    return `${base}/api/ws`;
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryCount.current = 0;
        // Authenticate
        if (userId) {
          ws.send(JSON.stringify({ type: "auth", user_id: userId }));
        }
      };

      ws.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === "pong" || event.type === "auth_ok") return;
          setLastEvent(event);
          // Notify module-specific listeners
          if (event.module) {
            const moduleListeners = listeners.current.get(event.module);
            if (moduleListeners) {
              moduleListeners.forEach((fn) => fn(event));
            }
          }
          // Notify wildcard listeners
          const wildcardListeners = listeners.current.get("*");
          if (wildcardListeners) {
            wildcardListeners.forEach((fn) => fn(event));
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Reconnect with backoff
        const delay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
        retryCount.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [getWsUrl, userId]);

  useEffect(() => {
    connect();
    // Ping keepalive every 30s
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  /**
   * Subscribe to events for a specific module (or "*" for all).
   * Returns unsubscribe function.
   */
  const subscribe = useCallback((module, callback) => {
    if (!listeners.current.has(module)) {
      listeners.current.set(module, new Set());
    }
    listeners.current.get(module).add(callback);
    return () => {
      listeners.current.get(module)?.delete(callback);
    };
  }, []);

  return { connected, lastEvent, subscribe };
}

// Named export for modules that import as { useWebSocket }
export { useWebSocket };
